<script setup>
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { pushToDataLayerDirectly } from '@/ui/scripts/analytics/analytics.methods';

import {
  ProjectsAlphabetList,
  ProjectsListitem,
  ProjectsRow,
  ProjectsTable,
} from '@/app/components';

const store = useStore();

const activeIndex = ref(-1);
const activeFocus = ref(-1);
const regionItems = ref([]);

const props = defineProps({
  projects: {
    type: Array,
    required: true,
  },
});

const filteredProjects = computed(() => {
  return props.projects.filter((cat) => cat.locations.length > 0);
});
const listViewIsAToZ = computed(() => store.state.projectsMap.listViewIsAToZ);

function focusOnButton() {
  regionItems.value[activeFocus.value].focusEl.focus();
}

function handleArrowKeys(event) {
  if (event.keyCode === 38 && activeFocus.value > 0) {
    activeFocus.value--;
  } else if (event.keyCode === 40 && activeFocus.value < filteredProjects.value.length - 1) {
    activeFocus.value++;
  }
  focusOnButton();
}

function handleTabKeyUp(event) {
  if (event.target.classList.contains('c-projects-listitem__button')) {
    activeFocus.value = props.projects.findIndex((project) => {
      return project.id === event.target.getAttribute('aria-controls');
    });
  }
}

function handleLetterClick(letter) {
  const index = filteredProjects.value.findIndex((proj) => proj.region === letter);
  if (activeIndex.value !== index) {
    activeFocus.value = activeIndex.value = activeIndex.value === index ? -1 : index;

    if (activeIndex.value > -1) {
      pushToDataLayerDirectly({
        event: 'projectMapProjectList_tracking',
        projectMapProjectList_action: `Click ${letter}`,
        projectMapProjectList_label: 'Open',
      });
    }
  }
}

function handleClick(index) {
  activeIndex.value = activeIndex.value === index ? -1 : index;

  if (activeIndex.value > -1) {
    activeFocus.value = activeIndex.value;
  }
}

watch(
  () => store.state.projectsMap.listViewIsAToZ,
  () => {
    activeIndex.value = -1;
    activeFocus.value = -1;
  },
);
</script>

<template>
  <ProjectsAlphabetList v-if="listViewIsAToZ" @click="handleLetterClick" :projects="projects" />
  <ul class="c-projects-list__wrapper" role="list">
    <template v-for="(region, regionIndex) in filteredProjects">
      <ProjectsListitem
        v-if="region.locations.length"
        @click="handleClick"
        @keydown.down.prevent="handleArrowKeys"
        @keydown.up.prevent="handleArrowKeys"
        @keyup.tab="handleTabKeyUp"
        :activeIndex="activeIndex"
        :id="region.id"
        :itemIndex="regionIndex"
        :region="region.region"
        :key="region.id"
        ref="regionItems"
      >
        <template v-slot:label>{{ region.region }}</template>
        <template v-slot:content>
          <ProjectsTable :index="regionIndex">
            <ProjectsRow
              v-for="project in region.locations"
              :key="project.uid"
              :project="project"
            />
          </ProjectsTable>
        </template>
      </ProjectsListitem>
    </template>
  </ul>
</template>

<style lang="scss">
.c-projects-list__wrapper {
  margin: 0;
  padding: 0;
}
</style>
